import React from 'react';
import ReactTooltip from 'react-tooltip';

import { EVENT_ADDON_ITEM_TYPE } from '../../../../../../../constants/event-items';
import { SEARCH_SUBSCRIPTION_PLAN } from '../../../../../../temp/views/onboarding/PhotoSearchCheckout/helpers';
import Button from '../../../../../../../components/global/Button';
import { formatPriceForDisplay } from '../../../../../../../util/format-money';
import CustomControl from '../../../../../../../components/global/CustomControl';
import Badge from '../../../../../../../components/global/Badge';
import InfoCircleIcon from '../../../../../../../components/icons/InfoCircleIcon';

export const FEATURE_SETS = {
  FREE: 'FREE',
  PLUS: 'PLUS',
  PREMIUM: 'PREMIUM',
  GOLD: 'GOLD',
};

const FEATURE_KEYS = {
  GREAT_FOR: 'GREAT_FOR',
  ALBUMS: 'ALBUMS',
  AI_TAGGING: 'AI_TAGGING',
  PRINTABLES: 'PRINTABLES',
  QR_CODE: 'QR_CODE',
  CUSTOM_SMS: 'CUSTOM_SMS',
  LIVE_SLIDESHOW: 'LIVE_SLIDESHOW',
  MODERATION: 'MODERATION',
  DISCOUNT: 'DISCOUNT',
  INVITES: 'INVITES',
  HOSTING: 'HOSTING',
  STORAGE: 'STORAGE',
};

export const BOX_FEATURES = {
  [FEATURE_SETS.GOLD]: [
    { key: FEATURE_KEYS.AI_TAGGING, value: 'AI photo tagging & delivery' },
    {
      key: FEATURE_KEYS.PRINTABLES,
      value: 'Printable QR signage (easy uploading!)',
    },
    { key: FEATURE_KEYS.CUSTOM_SMS, value: 'Custom SMS code for easy joining' },
    { key: FEATURE_KEYS.LIVE_SLIDESHOW, value: 'Live slideshow' },
    { key: FEATURE_KEYS.MODERATION, value: 'Contributor photo moderation' },
    { key: FEATURE_KEYS.INVITES, value: 'Bulk guest list import & invites' },
    {
      key: FEATURE_KEYS.STORAGE,
      value: (
        <>
          50 GB <b>event</b> storage
        </>
      ),
    },
  ],
  [FEATURE_SETS.PLUS]: [
    {
      key: FEATURE_KEYS.AI_TAGGING,
      value: (
        <>
          Every photo you&apos;re found in - <br className="d-md-none" />
          delivered!
          <span
            className="ml-1"
            data-for={`tooltip-plus-${FEATURE_KEYS.AI_TAGGING}`}
            data-tip=""
            data-event="click"
            data-effect="solid"
            data-place="right"
          >
            <InfoCircleIcon size={12} />

            <ReactTooltip
              id={`tooltip-plus-${FEATURE_KEYS.AI_TAGGING}`}
              className="tooltip"
              globalEventOff="click"
            >
              No more hunting and pecking through albums! Waldo will text or
              push alert you when any of up to 10 faces added to your profile
              are found.
            </ReactTooltip>
          </span>
        </>
      ),
    },
    {
      key: FEATURE_KEYS.LIVE_SLIDESHOW,
      value: (
        <>
          AI-powered slideshow builder
          <span
            className="ml-1"
            data-for={`tooltip-plus-${FEATURE_KEYS.LIVE_SLIDESHOW}`}
            data-tip=""
            data-event="click"
            data-effect="solid"
            data-place="right"
          >
            <InfoCircleIcon size={12} />

            <ReactTooltip
              id={`tooltip-plus-${FEATURE_KEYS.LIVE_SLIDESHOW}`}
              className="tooltip"
              globalEventOff="click"
            >
              Waldo&apos;s AI-powered slideshow builder will turn your photos
              into beautiful, collage-style slides at the press of a button!
            </ReactTooltip>
          </span>
        </>
      ),
    },
    {
      key: FEATURE_KEYS.DISCOUNT,
      value: <>15% off special events, prints, and postcards</>,
    },
    {
      key: FEATURE_KEYS.MODERATION,
      value: (
        <>
          Photo and member moderation
          <span
            className="ml-1"
            data-for={`tooltip-plus-${FEATURE_KEYS.MODERATION}`}
            data-tip=""
            data-event="click"
            data-effect="solid"
            data-place="right"
          >
            <InfoCircleIcon size={12} />

            <ReactTooltip
              id={`tooltip-plus-${FEATURE_KEYS.MODERATION}`}
              className="tooltip"
              globalEventOff="click"
            >
              Easily invite others to contribute photos and videos at your event
              while controlling who can add and view and which photos are
              published!
            </ReactTooltip>
          </span>
        </>
      ),
    },
    {
      key: FEATURE_KEYS.HOSTING,
      value: 'Photo and video hosting',
    },
    {
      key: FEATURE_KEYS.STORAGE,
      value: '200 GB of storage & unlimited galleries',
    },
  ],
  [FEATURE_SETS.PREMIUM]: [
    { key: FEATURE_KEYS.ALBUMS, value: 'Unlimited events' },
    {
      key: FEATURE_KEYS.AI_TAGGING,
      value: 'AI photo tagging & delivery up to 10 people',
    },
    { key: FEATURE_KEYS.MODERATION, value: 'Photo and video moderation' },
    { key: FEATURE_KEYS.DISCOUNT, value: '15% discount on prints' },
    { key: FEATURE_KEYS.STORAGE, value: '200GB storage (total)' },
  ],
};

/**
 * Given the everyDayShare & freeCreationEnabled values, determine the feature sets to use
 *
 * @param {Object} opts - the options
 * @param {Boolean} opts.isEveryDayShare - whether or not the event is for everyday share
 * @param {Boolean} opts.freeEventCreationEnabled - whether or not the user has free event creation available
 *
 * @returns {Array}
 */
export const getFeatureSets = ({
  isEveryDayShare,
  freeEventCreationEnabled,
}) => {
  if (isEveryDayShare && freeEventCreationEnabled) {
    return [FEATURE_SETS.FREE, FEATURE_SETS.PLUS];
  }

  if (isEveryDayShare) {
    return [FEATURE_SETS.PLUS];
  }

  if (freeEventCreationEnabled) {
    return [FEATURE_SETS.FREE, FEATURE_SETS.GOLD, FEATURE_SETS.PLUS];
  }

  return [FEATURE_SETS.GOLD, FEATURE_SETS.PLUS];
};

/**
 * Given a set, determine if it is currently selected
 *
 * @param {Object} opts the options
 * @param {String} opts.set the set to check for
 * @param {Object} opts.stepProps the current step props
 *
 * @returns {*|boolean}
 */
export const isSetSelected = ({ set, stepProps }) => {
  const {
    eventAddonItems,
    searchSubscriptionItem: selectedSearchSubscriptionItem,
    currentSelectedEventAddonItemIds,
  } = stepProps;

  const waldofiedEventAddonItem = eventAddonItems.find(eventAddonItem =>
    eventAddonItem.types.includes(EVENT_ADDON_ITEM_TYPE.WALDOFIED),
  );

  const plusSubSelected =
    selectedSearchSubscriptionItem?.plan ===
    SEARCH_SUBSCRIPTION_PLAN.SHUTTERBUG_PLUS;
  const premiumSubSelected =
    selectedSearchSubscriptionItem?.plan ===
    SEARCH_SUBSCRIPTION_PLAN.SHUTTERBUG_PREMIUM;
  const waldofiedSelected = currentSelectedEventAddonItemIds.includes(
    waldofiedEventAddonItem.id,
  );

  if (set === FEATURE_SETS.PLUS) {
    return plusSubSelected;
  }

  if (set === FEATURE_SETS.PREMIUM) {
    return premiumSubSelected;
  }

  return waldofiedSelected;
};

/**
 * Given a set of options, retrieve the header nodes
 *
 * @param {Object} opts - the options
 * @param {String} opts.set - The feature set to get the features for
 *
 * @returns {JSXElement}
 */
export const getBoxHeaderForSet = ({ set }) => {
  if (set === FEATURE_SETS.PLUS) {
    return (
      <div className="box-header mb-1">
        <h2 className="mb-0">Waldo Plus</h2>
        <span className="text-light text-sm">
          Private, authentic sharing made simple
        </span>
      </div>
    );
  }

  if (set === FEATURE_SETS.PREMIUM) {
    return (
      <div className="box-header">
        <Badge badgeStyle="yellow" badgeSize="lg">
          Popular!
        </Badge>
        <h2 className="mb-1">Premium</h2>
      </div>
    );
  }

  return (
    <div className="box-header">
      <h2 className="text-gold mb-1">Gold</h2>
    </div>
  );
};

/**
 * Given a set of options, retrieve the footer nodes
 *
 * @param {Object} opts - the options
 * @param {String} opts.set - The feature set to get the features for
 * @param {Array} opts.searchSubscriptionItems - an array containing subs data
 * @param {Object} opts.upgradeSelectionBoxesScroll - the parent scroll
 * @param {Object} opts.selectedSearchSubscriptionItem - the box selected search sub item
 * @param {Function} opts.setSelectedSearchSubscriptionItem - the box set selected search sub item
 * @param {Object} opts.stepProps - the current stepProps
 * @param {Function} opts.setStepProps - the set stepProps
 * @param {Function} opts.handleSubmit - the handleSubmit handler
 * @param {Boolean} opts.disableSubmit - whether or not to disable the the handleSubmit handler
 * @param {Function} opts.onSkip - the onSkip handler
 *
 * @returns {JSXElement}
 */
export const getBoxFooterForSet = ({
  set,
  searchSubscriptionItems,
  upgradeSelectionBoxesScroll,
  selectedSearchSubscriptionItem: boxSearchSubscriptionItem,
  setSelectedSearchSubscriptionItem: boxSetSelectedSearchSubscriptionItem,
  stepProps,
  setStepProps,
  handleSubmit,
  disableSubmit,
  onSkip,
}) => {
  const {
    eventAddonItems,
    onEventAddonItemRemove,
    onEventAddonItemToggle,
    freeTrialAvailable,
    isEveryDayShare,
  } = stepProps;

  const waldofiedEventAddonItem = eventAddonItems.find(eventAddonItem =>
    eventAddonItem.types.includes(EVENT_ADDON_ITEM_TYPE.WALDOFIED),
  );

  const plusSubSelected = isSetSelected({ set: FEATURE_SETS.PLUS, stepProps });
  const premiumSubSelected = isSetSelected({
    set: FEATURE_SETS.PREMIUM,
    stepProps,
  });
  const waldofiedSelected = isSetSelected({
    set: FEATURE_SETS.GOLD,
    stepProps,
  });

  if (set === FEATURE_SETS.PLUS) {
    const [yearlySub, monthlySub] = searchSubscriptionItems;
    const selectedSub =
      boxSearchSubscriptionItem === monthlySub ? monthlySub : yearlySub;

    return (
      <div className="box-footer">
        {!isEveryDayShare && (
          <p className="text-light text-sm mb-1">Unlimited standard events</p>
        )}
        <div className="text-primary text-sm text-normal mb-0">
          <CustomControl
            type="radio"
            size="sm"
            className="d-block"
            checked={boxSearchSubscriptionItem === yearlySub}
            label={
              <>
                <span className="text-extra-bold">
                  ${formatPriceForDisplay(yearlySub.price.value)}
                </span>{' '}
                / year (save 48%!)
              </>
            }
            onChange={() => {
              if (plusSubSelected) {
                onEventAddonItemRemove(waldofiedEventAddonItem.id);

                setStepProps({
                  ...stepProps,
                  subscriptionUpsellCompleted: true,
                  searchSubscriptionItem: yearlySub,
                  upgradeSelectionBoxesScroll,
                });
              }
              boxSetSelectedSearchSubscriptionItem(yearlySub);
            }}
          />
          <CustomControl
            type="radio"
            size="sm"
            className="d-block"
            checked={boxSearchSubscriptionItem === monthlySub}
            label={
              <>
                <span className="text-extra-bold">
                  ${formatPriceForDisplay(monthlySub.price.value)}
                </span>{' '}
                / month
              </>
            }
            onChange={() => {
              if (plusSubSelected) {
                onEventAddonItemRemove(waldofiedEventAddonItem.id);

                setStepProps({
                  ...stepProps,
                  subscriptionUpsellCompleted: true,
                  searchSubscriptionItem: monthlySub,
                  upgradeSelectionBoxesScroll,
                });
              }
              boxSetSelectedSearchSubscriptionItem(monthlySub);
            }}
          />
        </div>
        {isEveryDayShare && handleSubmit && (
          <div className="mt-auto text-center">
            <Button btnBlock onClick={handleSubmit} disabled={disableSubmit}>
              Start Plus
              {freeTrialAvailable && selectedSub.trial_days > 0
                ? ' Free Trial'
                : ''}
            </Button>
            {onSkip && (
              <Button
                btnStyle="link"
                btnBlock
                onClick={onSkip}
                disabled={disableSubmit}
              >
                No, thanks
              </Button>
            )}
          </div>
        )}
        {!isEveryDayShare && (
          <Button
            btnOutline={!plusSubSelected}
            btnBlock
            className="text-sm mt-auto"
            onClick={() => {
              onEventAddonItemRemove(waldofiedEventAddonItem.id);

              setStepProps({
                ...stepProps,
                subscriptionUpsellCompleted: true,
                searchSubscriptionItem: boxSearchSubscriptionItem,
                upgradeSelectionBoxesScroll,
              });
            }}
          >
            {plusSubSelected ? (
              <>
                {freeTrialAvailable && selectedSub.trial_days > 0
                  ? `Plus ${selectedSub.trial_days} day free trial selected`
                  : 'Plus selected'}
              </>
            ) : (
              <>
                Select Plus{' '}
                {freeTrialAvailable && selectedSub.trial_days > 0
                  ? `${selectedSub.trial_days} day free trial`
                  : ''}
              </>
            )}
          </Button>
        )}
      </div>
    );
  }

  if (set === FEATURE_SETS.PREMIUM) {
    const [yearlySub, monthlySub] = searchSubscriptionItems;
    const selectedSub =
      boxSearchSubscriptionItem === monthlySub ? monthlySub : yearlySub;

    return (
      <div className="box-footer">
        <div className="text-primary text-xs text-normal mb-0">
          <CustomControl
            type="radio"
            size="sm"
            className="d-block"
            checked={boxSearchSubscriptionItem === yearlySub}
            label={
              <>
                <span className="text-extra-bold">
                  ${formatPriceForDisplay(yearlySub.price.value)}
                </span>{' '}
                / year (save 48%!)
              </>
            }
            onChange={() => {
              if (premiumSubSelected) {
                onEventAddonItemRemove(waldofiedEventAddonItem.id);

                setStepProps({
                  ...stepProps,
                  subscriptionUpsellCompleted: true,
                  searchSubscriptionItem: yearlySub,
                  upgradeSelectionBoxesScroll,
                });
              }
              boxSetSelectedSearchSubscriptionItem(yearlySub);
            }}
          />
          <CustomControl
            type="radio"
            size="sm"
            className="d-block"
            checked={boxSearchSubscriptionItem === monthlySub}
            label={
              <>
                <span className="text-extra-bold">
                  ${formatPriceForDisplay(monthlySub.price.value)}
                </span>{' '}
                / month
              </>
            }
            onChange={() => {
              if (premiumSubSelected) {
                onEventAddonItemRemove(waldofiedEventAddonItem.id);

                setStepProps({
                  ...stepProps,
                  subscriptionUpsellCompleted: true,
                  searchSubscriptionItem: monthlySub,
                  upgradeSelectionBoxesScroll,
                });
              }
              boxSetSelectedSearchSubscriptionItem(monthlySub);
            }}
          />
        </div>
        <Button
          btnOutline={!premiumSubSelected}
          btnBlock
          className="text-sm mt-auto"
          onClick={() => {
            onEventAddonItemRemove(waldofiedEventAddonItem.id);

            setStepProps({
              ...stepProps,
              subscriptionUpsellCompleted: true,
              searchSubscriptionItem: boxSearchSubscriptionItem,
              upgradeSelectionBoxesScroll,
            });
          }}
        >
          {premiumSubSelected ? (
            <>
              {freeTrialAvailable && selectedSub.trial_days > 0
                ? `Premium ${selectedSub.trial_days} day free trial selected`
                : 'Premium selected'}
            </>
          ) : (
            <>
              Select Premium{' '}
              {freeTrialAvailable && selectedSub.trial_days > 0
                ? `${selectedSub.trial_days} day free trial`
                : ''}
            </>
          )}
        </Button>
      </div>
    );
  }

  return (
    <div className="box-footer">
      <p className="text-light text-sm mb-1">Single special event license</p>
      <span className="text-primary text-extra-bold text-sm mb-0">
        ${formatPriceForDisplay(waldofiedEventAddonItem.price.value)}
      </span>
      <Button
        btnOutline={!waldofiedSelected}
        btnBlock
        className="text-sm mt-auto"
        onClick={() => {
          onEventAddonItemToggle(waldofiedEventAddonItem.id);

          setStepProps({
            ...stepProps,
            subscriptionUpsellCompleted: true,
            searchSubscriptionItem: undefined,
            upgradeSelectionBoxesScroll,
          });
        }}
      >
        {waldofiedSelected ? 'Selected' : 'Select'}
      </Button>
    </div>
  );
};

/**
 * Given a set of options, retrieve the appropriate features for the given set
 *
 * @param {Object} opts - the options
 * @param {String} opts.set - The feature set to get the features for
 *
 * @returns {Array}
 */
export const getBoxFeaturesForSet = ({ set }) => {
  return BOX_FEATURES[set];
};

export const getSelectedSetIndex = ({
  sets,
  monthlySubscriptions,
  stepProps,
}) => {
  const {
    eventAddonItems,
    searchSubscriptionItem: selectedSearchSubscriptionItem,
    currentSelectedEventAddonItemIds,
  } = stepProps;

  const waldofiedEventAddonItem = eventAddonItems.find(eventAddonItem =>
    eventAddonItem.types.includes(EVENT_ADDON_ITEM_TYPE.WALDOFIED),
  );

  const monthlyPlusSub = monthlySubscriptions.find(
    sub => sub.plan === SEARCH_SUBSCRIPTION_PLAN.SHUTTERBUG_PLUS,
  );
  const monthlyPremiumSub = monthlySubscriptions.find(
    sub => sub.plan === SEARCH_SUBSCRIPTION_PLAN.SHUTTERBUG_PREMIUM,
  );

  const plusSubSelected =
    selectedSearchSubscriptionItem?.id === monthlyPlusSub?.id;
  const premiumSubSelected =
    selectedSearchSubscriptionItem?.id === monthlyPremiumSub?.id;
  const waldofiedSelected = currentSelectedEventAddonItemIds.includes(
    waldofiedEventAddonItem.id,
  );

  if (plusSubSelected) {
    return sets.indexOf(FEATURE_SETS.PLUS);
  }

  if (premiumSubSelected) {
    return sets.indexOf(FEATURE_SETS.PREMIUM);
  }

  if (waldofiedSelected) {
    return sets.indexOf(FEATURE_SETS.GOLD);
  }

  return sets.indexOf(FEATURE_SETS.FREE);
};
